import React, { createContext, useContext, useEffect, useState } from "react";
import { getRedirectResult } from "firebase/auth";
import { Capacitor } from "@capacitor/core";

import { auth } from "../firebase";
import {
  getCurrentAuthUser,
  subscribeToAuthChanges,
} from "../services/authDataService";
import { subscribeToInboxMergedData } from "../services/messagesDataService";

auth.languageCode = "de";

const AuthContext = createContext({
  user: null,
  authLoading: true,
  unreadCount: 0,
});

const SOCIAL_REDIRECT_KEY = "ee_post_social_redirect";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    let unsubscribeRef = null;
    let alive = true;

    const initAuth = async () => {
      try {
        if (!Capacitor.isNativePlatform()) {
          try {
            const result = await getRedirectResult(auth);

            if (!alive) return;

            if (result?.user) {
              const target =
                localStorage.getItem(SOCIAL_REDIRECT_KEY) || "/profil";

              localStorage.removeItem(SOCIAL_REDIRECT_KEY);
              window.location.replace(target);
              return;
            }
          } catch (err) {
            console.warn("getRedirectResult failed:", err);
          }
        }

        const currentUser = await getCurrentAuthUser();

        if (!alive) return;

        setUser(currentUser || null);

        unsubscribeRef = await subscribeToAuthChanges((nextUser) => {
          if (!alive) return;

          setUser(nextUser || null);
          setAuthLoading(false);
        });

        setAuthLoading(false);
      } catch (err) {
        console.warn("AuthProvider init failed:", err);

        if (!alive) return;

        setUser(null);
        setAuthLoading(false);
      }
    };

    initAuth();

    return () => {
      alive = false;

      if (typeof unsubscribeRef === "function") {
        const maybePromise = unsubscribeRef();

        if (maybePromise?.catch) {
          maybePromise.catch((err) =>
            console.warn("AuthProvider unsubscribe failed:", err)
          );
        }
      }
    };
  }, []);

  useEffect(() => {
    let unsubscribeRef = null;
    let alive = true;

    const initUnread = async () => {
      if (!user?.uid) {
        setUnreadCount(0);
        return;
      }

      try {
        unsubscribeRef = await subscribeToInboxMergedData(user.uid, (merged) => {
          if (!alive) return;

          const list = Array.isArray(merged) ? merged : [];

          const count = list.reduce((acc, m) => {
            const isUnread = m?.recipientId === user.uid && m?.read === false;
            return acc + (isUnread ? 1 : 0);
          }, 0);

          setUnreadCount(count);
        });
      } catch (err) {
        console.warn("unreadCount subscribe Fehler (AuthProvider):", err);

        if (alive) {
          setUnreadCount(0);
        }
      }
    };

    initUnread();

    return () => {
      alive = false;

      if (typeof unsubscribeRef === "function") {
        const maybePromise = unsubscribeRef();

        if (maybePromise?.catch) {
          maybePromise.catch((err) =>
            console.warn("AuthProvider unread unsubscribe failed:", err)
          );
        }
      }
    };
  }, [user?.uid]);

  return (
    <AuthContext.Provider value={{ user, authLoading, unreadCount }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}